import { useEffect, useRef } from "react";
import { useT } from "../i18n/I18nProvider";
import { Button } from "./Button";

interface Props {
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
}

/**
 * Infinite scroll: loads the next page when the sentinel nears the viewport. The visible button
 * does the same for keyboard and assistive-technology users and where IntersectionObserver is missing.
 */
export function LoadMore({ hasNextPage, isFetchingNextPage, fetchNextPage }: Props) {
  const t = useT();
  const sentinel = useRef<HTMLDivElement>(null);
  const latest = useRef({ hasNextPage, isFetchingNextPage, fetchNextPage });
  useEffect(() => {
    latest.current = { hasNextPage, isFetchingNextPage, fetchNextPage };
  });

  useEffect(() => {
    const node = sentinel.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const { hasNextPage: more, isFetchingNextPage: busy, fetchNextPage: next } = latest.current;
        if (entries.some((entry) => entry.isIntersecting) && more && !busy) void next();
      },
      { rootMargin: "400px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage]);

  if (!hasNextPage) return null;
  return (
    <div className="cc-load-more" ref={sentinel}>
      <Button onClick={() => void fetchNextPage()} busy={isFetchingNextPage} variant="secondary">
        {isFetchingNextPage ? t("common.loadingMore") : t("common.loadMore")}
      </Button>
    </div>
  );
}
